import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
import { useConfirm } from 'sutra/components/confirm/use-confirm'

import { handleException } from 'clients/exception-handling'
import { useFilterService } from 'features/filters/service'
import { useProjectStore } from 'features/project/store'
import { useTopBar } from 'features/project/top-bar'
import { AgentSharingModal } from 'features/trend-detection/components/AgentSharingModal/AgentSharingModal'
import { ISSUE_AGENTS_QUERY_KEY } from 'features/trend-detection/features/agent-list/hooks/use-agents-list'
import {
  IssueAgentDetailView,
  IssueAgentDetailViewLoading,
  IssueAgentDetailViewNotFound,
} from 'features/trend-detection/features/issue-agent-detail/components/IssueAgentDetailView/IssueAgentDetailView'
import { useAgent } from 'features/trend-detection/features/issue-agent-detail/hooks/use-agent'
import { useAgentRuns } from 'features/trend-detection/features/issue-agent-detail/hooks/use-agent-runs'
import { useFilterOptions } from 'features/trend-detection/features/issue-agent-detail/hooks/use-filter-options'
import {
  ISSUE_DETECTION_AGENT_TYPE,
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import {
  trendDetectionAgentCreatePath,
  trendDetectionAgentsPath,
  trendDetectionFeedPath,
} from 'features/trend-detection/paths'
import { useTrendDetectionService } from 'features/trend-detection/service'
import type { IssueAgentResponse } from 'features/trend-detection/types'
import type { ProjectState } from 'types/project'

/**
 * Issue Agent detail page — the "landing" surface when a user clicks an
 * agent row on the list: header + runs activity feed + metadata sidebar.
 * "Edit Agent" navigates to the unified create form in edit mode instead of
 * a route on this page — see `agent-create/Container.tsx`.
 */
export const IssueAgentDetailPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>()
  const navigate = useNavigate()
  const service = useTrendDetectionService()
  const filterService = useFilterService()
  const projectId = useProjectStore((s: ProjectState) => s.projectId)
  const queryClient = useQueryClient()

  // Resolves project / theme / dataset UUIDs to pretty names in AgentInfoCard's
  // Monitoring Scope chips (Trap #3).
  const { filterOptions } = useFilterOptions(filterService, projectId ?? null)

  const [isSharing, setIsSharing] = useState(false)
  const [isTogglingEmail, setIsTogglingEmail] = useState(false)
  const { setBreadcrumbSubtitle } = useTopBar()

  const { agent, isLoading: agentLoading } = useAgent(
    service,
    projectId,
    agentId ?? null,
  )

  const { runs, isLoading: runsLoading } = useAgentRuns(
    service,
    projectId,
    agentId ?? null,
  )

  const latestInsightsCount = useMemo(() => {
    const latest = runs.find((r) => r.status === 'completed')
    return latest?.issue_count ?? 0
  }, [runs])

  // Feed the agent's name into the app-shell breadcrumb ("Home / Agents / <name>").
  // Without this the breadcrumb falls back to the route's default label "Agent".
  useEffect(() => {
    const name = agent?.name ?? null
    setBreadcrumbSubtitle(name)
    return () => setBreadcrumbSubtitle(null)
  }, [agent?.name, setBreadcrumbSubtitle])

  const handleEdit = useCallback(() => {
    if (!projectId || !agentId) return
    navigate(
      `${trendDetectionAgentCreatePath(projectId)}?agent_type=issue_detection&agent_id=${agentId}`,
    )
  }, [projectId, agentId, navigate])

  const handleShare = useCallback(() => {
    setIsSharing(true)
  }, [])

  const handleToggleActive = useCallback(async () => {
    if (!projectId || !agentId || !agent) return
    const nextEnabled = !agent.is_enabled
    try {
      await service.setAgentEnabled(projectId, agentId, nextEnabled)
      await Promise.all([
        queryClient.invalidateQueries(['trend-agent', projectId, agentId]),
        queryClient.invalidateQueries([ISSUE_AGENTS_QUERY_KEY, projectId]),
      ])
      toast.success(nextEnabled ? 'Agent resumed.' : 'Agent paused.')
    } catch (cause) {
      handleException(cause, {
        operation: 'agent.issue.enabled.update',
        disposition: 'failed',
      })

      toast.error('Failed to update agent status.')
    }
  }, [projectId, agentId, agent, service, queryClient])

  const confirm = useConfirm()

  const handleDelete = useCallback(() => {
    if (!projectId || !agentId || !agent) return
    void confirm({
      title: `Delete ${agent.name}?`,
      content: 'This cannot be undone.',
      okText: 'Delete',
      danger: true,
      onOk: async () => {
        try {
          await service.deleteIssueAgent(projectId, agentId)
          await queryClient.invalidateQueries([
            ISSUE_AGENTS_QUERY_KEY,
            projectId,
          ])
          toast.success('Agent deleted.')
          navigate(trendDetectionAgentsPath(projectId))
        } catch (cause) {
          handleException(cause, {
            operation: 'agent.issue.delete',
            disposition: 'failed',
          })

          toast.error('Failed to delete agent.')
        }
      },
    })
  }, [confirm, projectId, agentId, agent, service, navigate, queryClient])

  const handleEmailNotificationsToggle = useCallback(
    async (checked: boolean) => {
      if (!projectId || !agentId) return
      setIsTogglingEmail(true)
      trackResearchAgents(ResearchAgentsAction.agent_email_preference_toggled, {
        agent_type: ISSUE_DETECTION_AGENT_TYPE,
        agent_uuid: agentId,
        agent_name: agent?.name ?? null,
        enabled: checked,
      })
      // Cancel any in-flight refetch so it can't overwrite the optimistic
      // write with the pre-mutation server state and slide the toggle back.
      await queryClient.cancelQueries(['trend-agent', projectId, agentId])
      const previous = queryClient.getQueryData<IssueAgentResponse>([
        'trend-agent',
        projectId,
        agentId,
      ])
      queryClient.setQueryData<IssueAgentResponse | undefined>(
        ['trend-agent', projectId, agentId],
        (old) => (old ? { ...old, email_notifications: checked } : old),
      )
      try {
        await service.updateEmailPreference(projectId, agentId, checked)
        toast.success(checked ? 'Emails enabled.' : 'Emails disabled.')
      } catch (cause) {
        handleException(cause, {
          operation: 'agent.issue.email.update',
          disposition: 'failed',
        })

        if (previous) {
          queryClient.setQueryData(
            ['trend-agent', projectId, agentId],
            previous,
          )
        }
        toast.error('Failed to update notification preference')
      } finally {
        setIsTogglingEmail(false)
      }
    },
    [projectId, agentId, agent, service, queryClient],
  )

  const getInsightsHref = useCallback(
    (recency: 'latest' | 'earlier') => {
      if (!projectId || !agentId) return '#'
      return `${trendDetectionFeedPath(projectId)}?agent_uuid=${agentId}&agent_type=issue_detection&insight_recency=${recency}`
    },
    [projectId, agentId],
  )

  const handlers = useMemo(
    () => ({
      onToggleActive: handleToggleActive,
      onShare: handleShare,
      onEdit: handleEdit,
      onDelete: handleDelete,
      getInsightsHref,
    }),
    [
      handleToggleActive,
      handleShare,
      handleEdit,
      handleDelete,
      getInsightsHref,
    ],
  )

  if (!projectId || !agentId) return null

  if (agentLoading) return <IssueAgentDetailViewLoading />
  if (!agent) return <IssueAgentDetailViewNotFound />

  return (
    <>
      <IssueAgentDetailView
        agent={agent}
        runs={runs}
        runsLoading={runsLoading}
        latestInsightsCount={latestInsightsCount}
        handlers={handlers}
        filterOptions={filterOptions}
        emailToggle={{
          checked: agent.email_notifications ?? false,
          onChange: handleEmailNotificationsToggle,
          disabled: isTogglingEmail,
        }}
      />
      <AgentSharingModal
        agentName={agent.name}
        agentUuid={agent.uuid}
        projectId={projectId}
        isEditor={agent.is_editor}
        open={isSharing}
        onClose={() => setIsSharing(false)}
        agentType="issue_detection"
      />
    </>
  )
}
